/**
 * The `relationships` section — What moves together (`docs/plans/macro.md`;
 * spec `docs/mockups/macro-v2.html` § 04).
 *
 * A 14-market correlation matrix of weekly changes over **13w · 52w · 3y**, or
 * its **Change (13w − 52w)** with breaks at the threshold outlined; a click on a
 * cell draws that pair's scatter with **β beside r** and its rolling
 * correlation. The Safe-haven correlations gauges were split out into their own
 * kind, `havens` (owner, 2026-11-02; `HavensSection.tsx`), with the warning line;
 * both read the one payload through `relationships-shared.tsx`.
 *
 * Every number is the nightly derive's (`api/macro-relationships.ts`); the pure
 * half is `lib/macro-relationships.ts`. Where the derive returned null for a
 * window (too little history), the cell and the scatter say
 * `NO_HISTORY` — nothing is struck client-side. The window and the matrix mode are the
 * section's config (persisted through `onConfig`); the selected pair is view
 * state (D8). Admin only — `MacroSectionHost` refuses
 * anyone else before this mounts.
 *
 * Colours are theme tokens (`useTokenResolver`) because SVG attributes need a
 * value; strength is drawn with `fill-opacity`, never a hand-written rgba.
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  fetchRelationshipPair,
  type MacroAsset,
  type PairPayload,
  type RelationshipsPayload,
} from "../../api/macro-relationships";
import {
  betaSentence,
  cellText,
  cellValue,
  corrOf,
  DEFAULT_ORDER,
  isBreak,
  NO_HISTORY,
  pairTables,
  REL_WINDOWS,
  relConfig,
  scatterOf,
  signed2,
  unitOf,
  WINDOW_WEEKS,
  yearBreaks,
  type RelMode,
  type RelWindow,
} from "../../lib/macro-relationships";
import { useWidth } from "./use-width";
import { macroScale } from "../../lib/macro-sections";
import { useTokenResolver } from "../theme";
import type { MacroSectionProps } from "./types";
import { errText, pathOf, S, Seg, useRelationshipsPayload, type Load } from "./relationships-shared";

export default function RelationshipsSection({ section, config, onConfig }: MacroSectionProps<"relationships">) {
  const { window: win, mode } = relConfig(config);
  // The stacked layout's drag-to-resize: chart heights times this
  // (`lib/macro-sections.ts`); widths stay measured, text 1:1.
  const scale = macroScale(config);
  const load = useRelationshipsPayload();
  const [pairSel, setPairSel] = useState<[string, string]>(["GOLD", "UUP"]);
  const data = load.state === "ready" ? load.data : null;

  const setConfig = (patch: Partial<{ window: RelWindow; mode: RelMode }>) =>
    onConfig?.({ ...config, ...patch });

  return (
    <div data-macro-kind="relationships" style={S.root}>
      <div style={S.head}>
        <strong style={S.title}>{section.title ?? "What moves together"}</strong>
        <Seg
          label="Window"
          value={win}
          options={REL_WINDOWS.map((w) => ({ v: w, text: w }))}
          onPick={(v) => setConfig({ window: v })}
        />
        <Seg
          label="Matrix"
          value={mode}
          options={[
            { v: "correlation" as const, text: "Correlation" },
            { v: "change" as const, text: "Change (13w − 52w)" },
          ]}
          onPick={(v) => setConfig({ mode: v })}
        />
        <span style={{ flex: 1 }} />
        <span style={S.dim}>weekly changes{data?.asOf ? ` · as of ${data.asOf}` : ""}</span>
      </div>
      {load.state === "loading" && <div style={S.note}>Loading relationships…</div>}
      {load.state === "error" && (
        <div role="alert" style={S.note}>
          {load.message}
        </div>
      )}
      {data && (
        <div style={S.body}>
          <div style={S.cmp}>
            <div style={S.box}>
              <Matrix data={data} mode={mode} win={win} pair={pairSel} onPick={setPairSel} scale={scale} />
            </div>
            <div style={S.box}>
              <PairView data={data} pair={pairSel} win={win} scale={scale} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// -- matrix ----------------------------------------------------------------------

function Matrix({
  data, mode, win, pair, onPick, scale,
}: {
  data: RelationshipsPayload;
  mode: RelMode;
  win: RelWindow;
  pair: [string, string];
  onPick: (p: [string, string]) => void;
  scale: number;
}) {
  const tok = useTokenResolver();
  const tables = useMemo(() => pairTables(data), [data]);
  const keys = data.assets.length ? data.assets.map((a) => a.key) : [...DEFAULT_ORDER];
  const chg = mode === "change";
  // Cells grow with the space (owner, 2026-11-01): one viewBox unit is one
  // pixel, so the grid fills its half and the text stays its size.
  const [boxRef, avail] = useWidth(420);
  const n = keys.length, L = 62, T = 62;
  const c = Math.max(25, Math.min(Math.round(46 * scale), Math.floor((avail - L - 4) / Math.max(1, n))));
  const W = L + n * c + 4, H = T + n * c + 35, fs = Math.max(9.5, c * 0.36 + 1);
  const thr = data.breakThreshold;
  const cells: ReactNode[] = [];
  keys.forEach((ka, i) =>
    keys.forEach((kb, j) => {
      const x = L + j * c, y = T + i * c;
      if (i === j) {
        cells.push(<rect key={`${ka}-${kb}`} x={x} y={y} width={c - 1} height={c - 1} rx={2} fill={tok("text")} fillOpacity={0.04} />);
        return;
      }
      const v = cellValue(tables, ka, kb, mode, win);
      const al = v == null ? 0 : Math.min(1, Math.abs(v));
      const sel = (pair[0] === ka && pair[1] === kb) || (pair[0] === kb && pair[1] === ka);
      const brk = isBreak(v, mode, thr);
      const hue = v == null ? "border" : chg ? (v >= 0 ? "accent.purple" : "warn") : v >= 0 ? "positive" : "negative";
      const none = v == null;
      const tip = chg
        ? `${ka} × ${kb}: 13w ${signed2(corrOf(tables, ka, kb, 13))} vs 52w ${signed2(corrOf(tables, ka, kb, 52))}${none ? ` · ${NO_HISTORY}` : ""}`
        : `${ka} × ${kb} (${win}): ${none ? NO_HISTORY : signed2(v)}`;
      cells.push(
        <g
          key={`${ka}-${kb}`}
          data-cell={`${ka}|${kb}`}
          data-break={brk || undefined}
          data-no-history={none || undefined}
          style={{ cursor: "pointer" }}
          onClick={() => onPick([ka, kb])}
        >
          <rect
            x={x}
            y={y}
            width={c - 1}
            height={c - 1}
            rx={2}
            fill={tok(hue)}
            fillOpacity={v == null ? 0.15 : 0.08 + al * 0.85}
            stroke={sel ? tok("text") : brk ? tok("accent.orange") : undefined}
            strokeWidth={sel ? 1.8 : brk ? 1.2 : undefined}
          />
          <text x={x + c / 2} y={y + c * 0.62} textAnchor="middle" style={{ fontSize: fs, fill: al > 0.45 ? tok("text") : tok("text.muted") }}>
            {none ? "n/a" : cellText(v, mode)}
          </text>
          <title>{tip}</title>
        </g>,
      );
    }),
  );
  return (
    <div ref={boxRef}>
    <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img" aria-label="Correlation matrix" style={{ display: "block", maxWidth: "100%" }}>
      {keys.map((k, i) => (
        <g key={k} style={{ fontSize: 10.5, fill: tok("text.secondary") }}>
          <text x={L - 5} y={T + i * c + c * 0.64} textAnchor="end">{k}</text>
          <text transform={`translate(${L + i * c + c * 0.64},${T - 5}) rotate(-60)`}>{k}</text>
        </g>
      ))}
      {cells}
      <text x={L} y={H - 4} style={{ fontSize: 10.5, fill: tok("text.muted") }}>
        {chg
          ? `purple = moving together more · orange = moving apart · outline = break ≥ ${thr.toFixed(1)}`
          : `teal = together · red = opposite · window ${win}`}
      </text>
      <text x={L} y={H - 4 - 13} style={{ fontSize: 10.5, fill: tok("text.muted") }}>
        n/a = {NO_HISTORY}
      </text>
    </svg>
    </div>
  );
}

// -- scatter + rolling correlation -------------------------------------------------

function PairView({ data, pair, win, scale }: { data: RelationshipsPayload; pair: [string, string]; win: RelWindow; scale: number }) {
  const [a, b] = pair;
  const key = `${a}|${b}`;
  const cache = useRef(new Map<string, PairPayload>());
  const [got, setGot] = useState<Load<PairPayload>>({ state: "loading" });
  useEffect(() => {
    const hit = cache.current.get(key);
    if (hit) {
      setGot({ state: "ready", data: hit });
      return;
    }
    let live = true;
    setGot({ state: "loading" });
    fetchRelationshipPair(a, b)
      .then((p) => {
        cache.current.set(key, p);
        if (live) setGot({ state: "ready", data: p });
      })
      .catch((e) => live && setGot({ state: "error", message: errText(e) }));
    return () => {
      live = false;
    };
  }, [key, a, b]);

  if (got.state === "loading") return <div style={S.note}>Loading {a} vs {b}…</div>;
  if (got.state === "error") return <div role="alert" style={S.note}>{got.message}</div>;
  const assets = new Map<string, MacroAsset>(data.assets.map((x) => [x.key, x]));
  return <PairCharts pair={got.data} a={assets.get(a)} b={assets.get(b)} aKey={a} bKey={b} win={win} scale={scale} />;
}

function PairCharts({
  pair, a, b, aKey, bKey, win, scale,
}: {
  pair: PairPayload;
  a: MacroAsset | undefined;
  b: MacroAsset | undefined;
  aKey: string;
  bKey: string;
  win: RelWindow;
  scale: number;
}) {
  const tok = useTokenResolver();
  const n = WINDOW_WEEKS[win];
  const sc = scatterOf(pair, n);
  // The measured width as the viewBox (owner, 2026-11-01): fills its half, text 1:1.
  const [boxRef, avail] = useWidth(400);
  const W = Math.max(320, avail), H = Math.round(Math.max(262, Math.min(460, Math.round(W * 0.5))) * scale), L = 44, R = W - 10, T = 40, B = H - 26;
  const xs = sc.points.map((p) => p.x), ys = sc.points.map((p) => p.y);
  const x0 = Math.min(...xs, 0), x1 = Math.max(...xs, 0), y0 = Math.min(...ys, 0), y1 = Math.max(...ys, 0);
  const X = (v: number) => L + ((v - x0) / (x1 - x0 || 1)) * (R - L);
  const Y = (v: number) => B - ((v - y0) / (y1 - y0 || 1)) * (B - T);
  const bt = sc.beta;

  const roll = pair.series[String(n)];
  const r52 = n !== 52 ? pair.series["52"] : undefined;
  const W2 = W, H2 = Math.round(120 * scale), L2 = 44, R2 = W2 - 10, T2 = 14, B2 = H2 - 16;
  const len = roll?.r.length ?? 0;
  const X2 = (i: number) => L2 + (i * (R2 - L2)) / Math.max(1, len - 1);
  const Y2 = (v: number) => T2 + ((1 - v) / 2) * (B2 - T2);
  const span = win === "3y" ? "3-year" : `${n}-week`;

  return (
    <div ref={boxRef}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={`${aKey} vs ${bKey} scatter`} style={{ display: "block" }}>
        <text x={L} y={14} style={{ fill: tok("text"), fontSize: 12 }} data-testid="pair-head" data-no-history={sc.noHistory || undefined}>
          {aKey} vs {bKey} · {win} ·{" "}
          {sc.noHistory ? (
            <tspan style={{ fill: tok("text.muted"), fontWeight: 600 }}>{NO_HISTORY}</tspan>
          ) : (
            <>
              r <tspan style={{ fill: tok((sc.r ?? 0) >= 0 ? "positive" : "negative"), fontWeight: 600 }}>{signed2(sc.r)}</tspan> · β{" "}
              <tspan style={{ fill: tok("warn"), fontWeight: 600 }}>{signed2(bt)}</tspan>
            </>
          )}
        </text>
        <text x={L} y={29} style={{ fontSize: 10.5, fill: tok("text.secondary") }}>
          {sc.noHistory ? `The derive has no ${win} r or β for this pair: the points below are fewer than the window needs.` : betaSentence(a, b, aKey, bKey, bt)}
        </text>
        {sc.points.length === 0 ? (
          <text x={L} y={(T + B) / 2} style={{ fontSize: 12, fill: tok("text.muted") }}>
            No complete {win} window for this pair yet.
          </text>
        ) : (
          <>
            {x0 < 0 && x1 > 0 && <line x1={X(0)} x2={X(0)} y1={T} y2={B} stroke={tok("axis")} />}
            {y0 < 0 && y1 > 0 && <line x1={L} x2={R} y1={Y(0)} y2={Y(0)} stroke={tok("axis")} />}
            {!sc.noHistory && bt != null && (
              <line
                x1={X(x0)} y1={Y(sc.meanY + bt * (x0 - sc.meanX))}
                x2={X(x1)} y2={Y(sc.meanY + bt * (x1 - sc.meanX))}
                stroke={tok("warn")} strokeWidth={1.5} strokeDasharray="5 3"
              />
            )}
            {sc.points.map((p, i) => {
              const last = i === sc.points.length - 1;
              return (
                <circle
                  key={p.week}
                  cx={X(p.x)}
                  cy={Y(p.y)}
                  r={last ? 5 : n > 100 ? 2.4 : 3.2}
                  fill={tok(last ? "text" : "accent")}
                  opacity={last ? 1 : 0.2 + (0.75 * i) / sc.points.length}
                >
                  <title>{`${p.week}: ${aKey} ${p.x.toFixed(2)} ${unitOf(a?.kind)}, ${bKey} ${p.y.toFixed(2)} ${unitOf(b?.kind)}`}</title>
                </circle>
              );
            })}
          </>
        )}
        <text x={R} y={H - 6} textAnchor="end" style={{ fontSize: 10.5, fill: tok("text.muted") }}>
          {aKey} ({unitOf(a?.kind)} wk) →
        </text>
        <text x={4} y={T + 8} style={{ fontSize: 10.5, fill: tok("text.muted") }}>↑ {bKey}</text>
      </svg>
      <svg viewBox={`0 0 ${W2} ${H2}`} width="100%" role="img" aria-label="Rolling correlation" style={{ display: "block", borderTop: "1px solid var(--qf-border)" }}>
        <text x={L2} y={10} style={{ fill: tok("text.secondary"), fontSize: 11 }}>
          Rolling {span} correlation{n !== 52 ? " · faint = 52w" : ""}
        </text>
        {[1, 0, -1].map((v) => (
          <g key={v}>
            <line x1={L2} x2={R2} y1={Y2(v)} y2={Y2(v)} stroke={tok(v ? "grid" : "axis")} />
            <text x={L2 - 5} y={Y2(v) + 3} textAnchor="end" style={{ fontSize: 10, fill: tok("text.muted") }}>
              {v > 0 ? "+1" : v < 0 ? "−1" : "0"}
            </text>
          </g>
        ))}
        {r52 && <path d={pathOf(r52.r, X2, Y2)} fill="none" stroke={tok("text.muted")} strokeWidth={1} />}
        {roll && <path d={pathOf(roll.r, X2, Y2)} fill="none" stroke={tok("accent")} strokeWidth={2.5} />}
        {roll &&
          yearBreaks(roll.week).map(({ i, year }) => (
            <text key={year} x={X2(i)} y={H2 - 3} style={{ fontSize: 10, fill: tok("text.muted") }}>{year}</text>
          ))}
      </svg>
    </div>
  );
}
